import {
  ShaderFitOptions,
  ShaderMount,
  getShaderColorFromString,
  heatmapFragmentShader,
  toProcessedHeatmap,
  type HeatmapUniforms,
  type ShaderMountUniforms
} from "@paper-design/shaders";
import { colors } from "./config/designTokens";

type ShaderHost = HTMLElement & {
  paperShaderMount?: ShaderMount;
};

const heatmapColors = [
  colors.blue,
  colors.sapphire,
  colors.sky,
  colors.green,
  colors.yellow,
  colors.red,
  colors.mauve
] as const;

let processedImageUrl: string | undefined;

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.decoding = "async";

    image.addEventListener("load", () => resolve(image), { once: true });
    image.addEventListener("error", () => reject(new Error(`Could not load shader image: ${src}`)), {
      once: true
    });

    image.src = src;
  });
}

function prefersReducedMotion(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function createHeatmapUniforms(image: HTMLImageElement): ShaderMountUniforms {
  const uniforms = {
    u_image: image,
    u_fit: ShaderFitOptions.contain,
    u_scale: 0.86,
    u_rotation: 0,
    u_originX: 0.5,
    u_originY: 0.5,
    u_offsetX: 0,
    u_offsetY: 0,
    u_worldWidth: 0,
    u_worldHeight: 0,
    u_contour: 0.62,
    u_angle: 124,
    u_noise: 0,
    u_innerGlow: 0.39,
    u_outerGlow: 0.05,
    u_colorBack: getShaderColorFromString("rgba(17, 17, 27, 0)"),
    u_colors: heatmapColors.map((color) => getShaderColorFromString(color)),
    u_colorsCount: heatmapColors.length
  } satisfies HeatmapUniforms;

  return uniforms;
}

export async function mountHeroShader(): Promise<void> {
  const container = document.querySelector<ShaderHost>("[data-hero-shader]");

  if (!container || container.dataset.shaderState) {
    return;
  }

  container.dataset.shaderState = "loading";

  try {
    const imageSrc = container.dataset.imageSrc;

    if (!imageSrc) {
      throw new Error("Missing hero shader image source");
    }

    const processedImage = await toProcessedHeatmap(imageSrc);
    processedImageUrl = URL.createObjectURL(processedImage.blob);

    const image = await loadImage(processedImageUrl);
    container.paperShaderMount?.dispose();

    new ShaderMount(
      container,
      heatmapFragmentShader,
      createHeatmapUniforms(image),
      { alpha: true, premultipliedAlpha: true },
      prefersReducedMotion() ? 0 : 0.7,
      0,
      1.25,
      1280 * 960
    );

    container.dataset.shaderState = "ready";
    container.classList.add("is-mounted");
  } catch (error) {
    container.dataset.shaderState = "fallback";
    container.classList.add("is-fallback");
    console.warn("Foxmark hero shader could not be mounted.", error);
  }
}

export function disposeHeroShader(): void {
  const container = document.querySelector<ShaderHost>("[data-hero-shader]");

  container?.paperShaderMount?.dispose();

  if (processedImageUrl) {
    URL.revokeObjectURL(processedImageUrl);
    processedImageUrl = undefined;
  }
}

window.addEventListener("pagehide", disposeHeroShader);

if (import.meta.hot) {
  import.meta.hot.dispose(disposeHeroShader);
}
